import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Table } from "antd";
import Breadcrumbs from "../../../components/Breadcrumbs";
import LoanFormModal from "../../../components/modelpopup/LoanFormModal";
import { useLazyGetMyLoansQuery, useCreateLoanMutation } from "../../../services/loan.service";
import { useSelector } from "react-redux";
import { formatNumber } from "../../../utils";
import dayjs from "dayjs";
import alertify from "alertifyjs";

const MyLoans = () => {
  const [selected, setSelected] = useState(null);
  const [pagination, setPagination] = useState({});
  const [results, setResult] = useState([]);
  const { user } = useSelector((state) => state.user);

  const [getMyLoans, { data, isFetching }] = useLazyGetMyLoansQuery();
  const [createLoan, { isLoading: isCreating }] = useCreateLoanMutation();

  useEffect(() => {
    getMyLoans({ page: 1, limit: 10 });
  }, []);

  useEffect(() => {
    if (data) {
      setResult(data?.data?.result || []);
      setPagination(data?.data?.meta || {});
    }
  }, [data]);

  const handleSubmit = async (loanData) => {
    try {
      const result = await createLoan({
        ...loanData,
        employee_id: user?.emp_id,
      }).unwrap();

      if (result.success) {
        alertify.success(result.message || "Loan request submitted successfully");
        getMyLoans({ page: 1, limit: 20 });
        setSelected(null);
      }
    } catch (error) {
      alertify.error(error?.data?.message || "Failed to submit loan request");
    }
  };

  // Calculate statistics from current employee's loans
  const stats = {
    totalAmount: results
      .filter((l) => ["Approved", "Disbursed", "Repaying"].includes(l.status))
      .reduce((sum, loan) => sum + parseFloat(loan.amount || 0), 0),
    totalPaid: results.reduce((sum, loan) => sum + parseFloat(loan.total_paid || 0), 0),
    pending: results.filter((l) => l.status === "Pending" || l.status === "New").length,
    approved: results.filter((l) => l.status === "Approved").length,
    rejected: results.filter((l) => l.status === "Rejected").length,
    active: results.filter((l) => ["Disbursed", "Repaying"].includes(l.status)).length,
  };

  const statsData = [
    {
      title: "Total Loan Amount",
      value: formatNumber(stats.totalAmount),
      icon: "la la-money",
      color: "primary",
    },
    {
      title: "Total Paid",
      value: formatNumber(stats.totalPaid),
      icon: "la la-check-circle",
      color: "success",
    },
    {
      title: "Pending Requests",
      value: stats.pending,
      icon: "la la-clock-o",
      color: "warning",
    },
    {
      title: "Active Loans",
      value: stats.active,
      icon: "la la-refresh",
      color: "info",
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "New":
        return "purple";
      case "Pending":
        return "info";
      case "Approved":
        return "success";
      case "Disbursed":
        return "primary";
      case "Repaying":
        return "warning";
      case "Completed":
        return "success";
      case "Rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      render: (t, r, i) => i + 1,
      width: 50,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (text) => <span className="fw-bold">₦{formatNumber(text)}</span>,
      sorter: (a, b) => parseFloat(a.amount) - parseFloat(b.amount),
    },
    {
      title: "Tenure (Months)",
      dataIndex: "tenure",
      render: (text) => <span>{text} months</span>,
      sorter: (a, b) => a.tenure - b.tenure,
    },
    {
      title: "Monthly Repayment",
      dataIndex: "monthly_repayment",
      render: (text) => <span>₦{formatNumber(text)}</span>,
      sorter: (a, b) => parseFloat(a.monthly_repayment) - parseFloat(b.monthly_repayment),
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      render: (text) => <span>{text ? dayjs(text).format("DD MMM YYYY") : "N/A"}</span>,
      sorter: (a, b) => new Date(a.start_date) - new Date(b.start_date),
    },
    {
      title: "Outstanding",
      dataIndex: "outstanding_balance",
      render: (text) => <span className="text-danger">₦{formatNumber(text)}</span>,
      sorter: (a, b) => parseFloat(a.outstanding_balance) - parseFloat(b.outstanding_balance),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text) => (
        <span className={`badge bg-inverse-${getStatusColor(text)}`}>
          {text}
        </span>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      render: (text) => (
        <span className="text-truncate" style={{ maxWidth: "200px", display: "inline-block" }}>
          {text || "N/A"}
        </span>
      ),
    },
    {
      title: "Remarks",
      dataIndex: "approval_remarks",
      render: (text) => (
        <span className="text-truncate" style={{ maxWidth: "150px", display: "inline-block" }}>
          {text || "-"}
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <Breadcrumbs
            maintitle="My Loans"
            title="Dashboard"
            subtitle="My Loans"
            addButton={() => (
              <Link
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#loans_modal"
                className="btn add-btn"
                onClick={() => setSelected(null)}
              >
                <i className="fa fa-plus" /> Request Loan
              </Link>
            )}
          />

          {/* Statistics */}
          <div className="row">
            {statsData.map((stat, index) => (
              <div className="col-md-6 col-sm-6 col-lg-6 col-xl-3" key={index}>
                <div className="card dash-widget">
                  <div className="card-body">
                    <span className="dash-widget-icon">
                      <i className={stat.icon} />
                    </span>
                    <div className="dash-widget-info">
                      <h3>{stat.value}</h3>
                      <span>{stat.title}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Loan Table */}
          <div className="row">
            <div className="col-md-12">
              <div className="table-responsive">
                <Table
                  className="table-striped"
                  columns={columns}
                  loading={isFetching}
                  dataSource={results}
                  rowKey={(record) => record.id}
                  pagination={{
                    defaultPageSize: 10,
                    total: pagination?.total || results.length,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                    onChange: (page, pageSize) => {
                      getMyLoans({ page, limit: pageSize });
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loan Form Modal */}
      <LoanFormModal
        item={selected}
        onSubmit={handleSubmit}
        isLoading={isCreating}
        employeeId={user?.emp_id}
        isEmployeeView={true}
      />
    </>
  );
};

export default MyLoans;
